<template>
  <v-card class="pyg-chart-card elevation-12 rounded-top">
    <v-card-title class="pb-2">
      <span>Distribución de Resultados</span>
      <span v-if="fiscalYearsText" class="subtitle-1 ml-2 text--secondary">
        ({{ fiscalYearsText }})
      </span>
    </v-card-title>

    <v-card-text>
      <div v-if="hasChartData" class="chart-container">
        <apexchart
          type="bar"
          height="400"
          :options="chartOptions"
          :series="chartSeries"
        ></apexchart>
      </div>

      <div v-else class="no-data-message">
        <v-icon large color="grey lighten-1">mdi-chart-bar</v-icon>
        <div class="no-data-text">
          No hay datos disponibles para {{ isMultiYear ? 'los ejercicios seleccionados' : 'el ejercicio seleccionado' }}
        </div>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
import VueApexCharts from 'vue-apexcharts';
import { formatCurrency, formatPercentage } from '@/utils/pygFormatters';

export default {
  name: 'PygChart',

  components: {
    apexchart: VueApexCharts
  },

  props: {
    chartData: {
      type: [Array, Object],
      required: true,
      default: () => []
    },
    selectedEjercicio: {
      type: Number,
      required: false,
      default: null
    },
    selectedEjercicios: {
      type: Array,
      default: () => []
    },
    isMultiYear: {
      type: Boolean,
      default: false
    }
  },

  computed: {
    /**
     * Display text for fiscal years
     */
    fiscalYearsText() {
      if (this.isMultiYear && this.selectedEjercicios && this.selectedEjercicios.length > 0) {
        return this.selectedEjercicios.join(', ');
      } else if (this.selectedEjercicio) {
        return String(this.selectedEjercicio);
      }
      return '';
    },

    /**
     * Check if there's chart data to display
     */
    hasChartData() {
      if (this.isMultiYear) {
        // For multi-year, chartData is an object like {2023: [...], 2024: [...]}
        if (!this.chartData || typeof this.chartData !== 'object') return false;
        const years = Object.keys(this.chartData);
        return years.length > 0 && years.some(year => {
          const yearData = this.chartData[year];
          return Array.isArray(yearData) && yearData.length > 0;
        });
      } else {
        // For single-year, chartData is an array
        return Array.isArray(this.chartData) && this.chartData.length > 0;
      }
    },

    /**
     * Get all unique concepts across all years (for multi-year mode)
     */
    allConcepts() {
      if (!this.isMultiYear) return [];

      const conceptsSet = new Set();
      const sortedYears = [...this.selectedEjercicios].sort((a, b) => a - b);

      sortedYears.forEach(year => {
        const yearData = this.chartData[year];
        if (Array.isArray(yearData)) {
          yearData.forEach(segment => {
            conceptsSet.add(segment.concept);
          });
        }
      });

      return Array.from(conceptsSet);
    },

    /**
     * Get color map for concepts
     */
    conceptColorMap() {
      const colorMap = {};

      if (this.isMultiYear) {
        // Build color map from all years
        const sortedYears = [...this.selectedEjercicios].sort((a, b) => a - b);
        sortedYears.forEach(year => {
          const yearData = this.chartData[year];
          if (Array.isArray(yearData)) {
            yearData.forEach(segment => {
              if (!colorMap[segment.concept]) {
                colorMap[segment.concept] = segment.color;
              }
            });
          }
        });
      } else {
        // Build color map from single year
        if (Array.isArray(this.chartData)) {
          this.chartData.forEach(segment => {
            colorMap[segment.concept] = segment.color;
          });
        }
      }

      return colorMap;
    },

    chartOptions() {
      return {
        chart: {
          type: 'bar',
          stacked: true,
          stackType: '100%',
          toolbar: {
            show: true
          },
          foreColor: '#333',
          background: 'transparent'
        },
        plotOptions: {
          bar: {
            horizontal: true,
            barHeight: '70%',
            dataLabels: {
              total: {
                enabled: false
              }
            }
          }
        },
        xaxis: {
          categories: this.isMultiYear
            ? this.selectedEjercicios.map(year => `Ejercicio ${year}`)
            : [`Ejercicio ${this.selectedEjercicio}`],
          labels: {
            formatter: (value) => {
              return `${value.toFixed(1)}%`;
            },
            style: {
              colors: '#333',
              fontSize: '12px'
            }
          },
          min: 0,
          max: 100,
          tickAmount: 10
        },
        yaxis: {
          labels: {
            style: {
              colors: '#333',
              fontSize: '14px',
              fontWeight: 500
            }
          }
        },
        dataLabels: {
          enabled: true,
          formatter: (value, { seriesIndex }) => {
            // Only show percentage if it's significant enough (> 3%)
            if (value > 3) {
              return `${value.toFixed(1)}%`;
            }
            return '';
          },
          style: {
            fontSize: '12px',
            fontWeight: 'bold',
            colors: ['#fff']
          },
          dropShadow: {
            enabled: false
          }
        },
        colors: this.isMultiYear
          ? this.allConcepts.map(concept => this.conceptColorMap[concept] || '#95A5A6')
          : this.chartData.map(segment => segment.color),
        legend: {
          position: 'bottom',
          horizontalAlign: 'center',
          fontSize: '13px',
          fontWeight: 500,
          labels: {
            colors: '#333'
          },
          markers: {
            width: 14,
            height: 14,
            radius: 2
          },
          itemMargin: {
            horizontal: 12,
            vertical: 8
          }
        },
        tooltip: {
          theme: 'light',
          y: {
            formatter: (value, { seriesIndex, dataPointIndex }) => {
              if (this.isMultiYear) {
                // Multi-year mode: get concept from allConcepts[seriesIndex]
                // and year data from chartData[year][conceptIndex]
                const concept = this.allConcepts[seriesIndex];
                const sortedYears = [...this.selectedEjercicios].sort((a, b) => a - b);
                const year = sortedYears[dataPointIndex];
                const yearData = this.chartData[year];

                if (yearData && Array.isArray(yearData)) {
                  const segment = yearData.find(s => s.concept === concept);
                  if (segment) {
                    return `
                      <div style="padding: 2px 0;">
                        <strong>${segment.concept}</strong><br/>
                        Año: ${year}<br/>
                        Importe: ${formatCurrency(segment.value, false)} €<br/>
                        Porcentaje: ${formatPercentage(value)}
                      </div>
                    `;
                  }
                }
              } else {
                // Single-year mode: original behavior
                const segment = this.chartData[seriesIndex];
                if (segment) {
                  return `
                    <div style="padding: 2px 0;">
                      <strong>${segment.concept}</strong><br/>
                      Importe: ${formatCurrency(segment.value, false)} €<br/>
                      Porcentaje: ${formatPercentage(value)}
                    </div>
                  `;
                }
              }
              return `${value.toFixed(1)}%`;
            }
          },
          x: {
            show: false
          }
        },
        grid: {
          borderColor: '#e0e0e0',
          strokeDashArray: 4,
          xaxis: {
            lines: {
              show: true
            }
          },
          yaxis: {
            lines: {
              show: false
            }
          }
        },
        responsive: [
          {
            breakpoint: 600,
            options: {
              plotOptions: {
                bar: {
                  barHeight: '60%'
                }
              },
              dataLabels: {
                enabled: false
              },
              legend: {
                fontSize: '11px',
                itemMargin: {
                  horizontal: 8,
                  vertical: 6
                }
              }
            }
          }
        ]
      };
    },

    chartSeries() {
      if (this.isMultiYear) {
        // MULTI-YEAR MODE: Each concept becomes a series with multiple data points (one per year)
        const sortedYears = [...this.selectedEjercicios].sort((a, b) => a - b);

        return this.allConcepts.map(concept => {
          // Build data array with one value per year
          const data = sortedYears.map(year => {
            const yearData = this.chartData[year];
            if (Array.isArray(yearData)) {
              const segment = yearData.find(s => s.concept === concept);
              return segment ? segment.percentage : 0;
            }
            return 0;
          });

          return {
            name: concept,
            data: data
          };
        });
      } else {
        // SINGLE-YEAR MODE: Each segment becomes a series with single data point
        return this.chartData.map(segment => ({
          name: segment.concept,
          data: [segment.percentage]
        }));
      }
    }
  }
}
</script>

<style scoped>
.pyg-chart-card {
  margin-bottom: 24px;
  border: 1px solid #e0e0e0;
  background-color: #ffffff;
}

.pyg-chart-card .v-card__title {
  color: var(--v-primary-base);
  font-size: 18px;
  font-weight: 500;
  border-bottom: 1px solid #e6e6e6;
  padding: 16px 20px;
}

.chart-container {
  width: 100%;
  min-height: 400px;
  padding: 20px 0;
}

.no-data-message {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  padding: 20px;
  text-align: center;
  background-color: var(--v-neutral-base);
  border-radius: 8px;
  border: 1px dashed #e0e0e0;
}

.no-data-message .v-icon {
  margin-bottom: 16px;
  opacity: 0.7;
}

.no-data-text {
  color: #757575;
  font-size: 16px;
  font-weight: 400;
  max-width: 80%;
  margin: 0 auto;
}

/* Responsive adjustments */
@media (max-width: 960px) {
  .chart-container {
    min-height: 350px;
    padding: 15px 0;
  }

  .pyg-chart-card .v-card__title {
    font-size: 16px;
    padding: 12px 16px;
  }
}

@media (max-width: 600px) {
  .chart-container {
    min-height: 300px;
    padding: 10px 0;
  }

  .no-data-message {
    min-height: 300px;
  }

  .no-data-text {
    font-size: 14px;
  }
}
</style>
